import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleRedirectService } from 'app/core/auth/services/role-redirect.service';
import {
  incompleteOnboardingUrl,
  miniTenantAwaitingApproval,
  userHasMiniStoreAdminRole,
} from 'app/core/auth/utils/mini-app-admin.utils';
import { of, switchMap, catchError } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * OnboardingGuard - Protects routes from un-onboarded users
 * 
 * This guard checks if the user has completed onboarding.
 * - If user is not authenticated, redirects to sign-in
 * - If user is authenticated but hasn't completed onboarding, redirects to onboarding
 * - If user has completed onboarding and tries to access onboarding routes, redirects to default page
 * - If user has completed onboarding, allows access to non-onboarding routes
 * - Super Admins always have access (they skip onboarding)
 */
export const OnboardingGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router: Router = inject(Router);
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const roleRedirectService = inject(RoleRedirectService);

  // First check if user is authenticated
  return authService.check().pipe(
    switchMap((authenticated) => {
      if (!authenticated) {
        // User is not authenticated, redirect to sign-in
        const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
        const urlTree = router.parseUrl(`auth/sign-in?${redirectURL}`);
        return of(urlTree);
      }

      // User is authenticated, check onboarding status
      return userService.user$.pipe(
        take(1), // Take only the first emission to avoid memory leaks
        switchMap((user) => {
          if (!user) {
            // User data not loaded yet, allow access (will be checked again when data loads)
            // This can happen during initial app load
            return of(true);
          }

          if (user.mustResetPassword && !state.url.split('?')[0].startsWith('/auth/reset-password')) {
            return of(router.parseUrl('/auth/reset-password?flow=temporary'));
          }

          // Super Admin should NEVER be blocked (they skip onboarding)
          const isSuperAdmin = user.roles?.some(
            (role: any) => role.roleKey === 'super-admin' || 
                           role.role?.toLowerCase() === 'super admin'
          );

          // Check if accessing admin management routes (tenant/organization/store)
          // These are administration features, not onboarding flows
          const isAdminManagementRoute = state.url.startsWith('/on-boarding/tenant') ||
                                        state.url.startsWith('/on-boarding/organization') ||
                                        state.url.startsWith('/on-boarding/store');

          // Super Admin always has access to admin management routes
          if (isSuperAdmin && isAdminManagementRoute) {
            return of(true); // Super Admin can always access tenant/organization/store management
          }

          if (isSuperAdmin) {
            return of(true); // Super Admin always has access to everything else
          }

          // Unapproved mini tenants stay on the store step even if onboardingComplete was set earlier.
          if (
            miniTenantAwaitingApproval(user) ||
            (!user.onboardingComplete && !userHasMiniStoreAdminRole(user.roles))
          ) {
            const onboardingUrl = incompleteOnboardingUrl(user);
            const waitingForApproval = onboardingUrl.includes('sign-up-store');
            if (waitingForApproval) {
              if (state.url.includes('sign-up-store')) {
                return of(true);
              }
              return of(router.parseUrl(onboardingUrl));
            }

            // User hasn't completed onboarding, redirect to onboarding page
            // But allow access to onboarding routes themselves
            const isOnboardingRoute = state.url.startsWith('/on-boarding') || 
                                     route.routeConfig?.path?.includes('on-boarding');
            if (isOnboardingRoute) {
              return of(true); // Allow access to onboarding routes
            }
            
            // Redirect to onboarding
            console.log('🛡️ OnboardingGuard: User has not completed onboarding, redirecting...');
            return of(router.parseUrl(onboardingUrl));
          }

          // User has completed onboarding
          // Check if they're trying to access onboarding routes
          const isOnboardingRoute = state.url.startsWith('/on-boarding') || 
                                   route.routeConfig?.path?.includes('on-boarding');
          if (isOnboardingRoute && !isAdminManagementRoute) {
            // User has completed onboarding but is trying to access onboarding routes (not admin management)
            // Redirect them to their default page
            const defaultRedirectUrl = roleRedirectService.getDefaultRedirectUrl();
            console.log('🛡️ OnboardingGuard: User has completed onboarding, redirecting from onboarding route to:', defaultRedirectUrl);
            return of(router.parseUrl(defaultRedirectUrl));
          }

          // User has completed onboarding and is accessing non-onboarding routes, allow access
          return of(true);
        }),
        catchError((error) => {
          console.error('❌ OnboardingGuard error:', error);
          // On error, redirect to sign-in to be safe
          const urlTree = router.parseUrl('auth/sign-in');
          return of(urlTree);
        })
      );
    }),
    catchError((error) => {
      console.error('❌ OnboardingGuard auth check error:', error);
      // On error, redirect to sign-in to be safe
      const urlTree = router.parseUrl('auth/sign-in');
      return of(urlTree);
    })
  );
};

